import { call, put, select, takeLatest } from 'redux-saga/effects';

import LabOrderService from 'shared/services/LabOrderService';
import { setPatientLabOrders } from 'store/patients/patients.redux';
import {
  ILabOrder,
  ICreateUpdateLabOrder
} from 'shared/interfaces/labOrder.interface';
import {
  TAction,
  IOrdersGetData,
  TPaginationResponse,
  TNoteCreateAction
} from 'shared/types';
import {
  getLabOrders,
  setLabOrders,
  getLabOrder,
  setLabOrder,
  failAction,
  addNewNote,
  removeNote,
  createLabOrderNote,
  deleteLabOrderNote,
  createLabOrder,
  updateLabOrder
} from './labOrders.redux';
import { setOrderLabOrders } from 'store/orders/orders.redux';
import { IState } from 'shared/interfaces/state.interface';
import { INote } from 'shared/interfaces/note.interface';
import NoteService from 'shared/services/NoteService';
import { openNotificationMessage } from 'shared/utils/NotificationMessages';
import { NotificationMessages } from 'shared/enums';

function* getLabOrdersSaga({ payload }: TAction<IOrdersGetData>) {
  try {
    const result: TPaginationResponse<ILabOrder[]> = yield call(
      LabOrderService.getLabOrders,
      payload
    );

    yield put(setLabOrders(result));
  } catch (error) {
    console.log('getLabOrdersSaga error', error);
    yield put(failAction());
  }
}

function* createLabOrderSaga({ payload }: TAction<ICreateUpdateLabOrder>) {
  try {
    const result: ILabOrder = yield call(
      LabOrderService.createLabOrder,
      payload
    );

    yield put(setLabOrder(result));
    yield put(setPatientLabOrders(result));
    yield put(setOrderLabOrders(result));
    yield openNotificationMessage({
      type: NotificationMessages.Success,
      title: 'Success',
      message: 'Lab Order was successfully created.'
    });
  } catch (error) {
    console.log('error', error);
    yield put(failAction());
    yield openNotificationMessage({
      type: NotificationMessages.Error,
      title: 'Error',
      message: 'Lab Order creation was unsuccessful.'
    });
  }
}

function* getLabOrderSaga({ payload }: TAction<{ id: string }>) {
  try {
    const labOrder: ILabOrder = yield call(
      LabOrderService.getLabOrder,
      payload.id
    );
    yield put(setLabOrder(labOrder));
  } catch (error) {
    console.log('getLabOrderSaga error', error);
    yield put(failAction());
  }
}

function* createNoteSaga({ payload }: TAction<TNoteCreateAction>) {
  try {
    const currentLabOrder: ILabOrder = yield select(
      (state: IState) => state.labOrders.currentLabOrder
    );

    const result: INote = yield call(NoteService.createNote, {
      ...payload,
      relatedEntity: currentLabOrder.id
    });

    yield put(addNewNote(result));
    yield openNotificationMessage({
      type: NotificationMessages.Success,
      title: 'Success',
      message: 'Note was successfully added.'
    });
  } catch (error) {
    console.log('error', error);
    yield openNotificationMessage({
      type: NotificationMessages.Error,
      title: 'Error',
      message: 'Note creation was unsuccessful.'
    });
  }
}

function* deleteNoteSaga({ payload }: TAction<string>) {
  try {
    yield call(NoteService.deleteNote, payload);
    yield put(removeNote(payload));
    yield openNotificationMessage({
      type: NotificationMessages.Success,
      title: 'Success',
      message: 'Note was successfully deleted.'
    });
  } catch (error: any) {
    console.log('error', error);
    yield openNotificationMessage({
      type: NotificationMessages.Error,
      title: 'Error',
      message: error?.response?.data.message || 'Unable to delete note.'
    });
  }
}

function* updateLabOrderSaga({ payload }: TAction<ICreateUpdateLabOrder>) {
  try {
    const result: ILabOrder = yield call(
      LabOrderService.updateLabOrder,
      payload
    );

    yield put(setLabOrder(result));
    yield openNotificationMessage({
      type: NotificationMessages.Success,
      title: 'Success',
      message: 'Lab Order was successfully updated.'
    });
  } catch (error) {
    console.log('error', error);
    yield put(failAction());
    yield openNotificationMessage({
      type: NotificationMessages.Error,
      title: 'Error',
      message: 'Lab Order was not updated.'
    });
  }
}

function* Orders() {
  yield takeLatest(getLabOrders.type, getLabOrdersSaga);
  yield takeLatest(createLabOrder.type, createLabOrderSaga);
  yield takeLatest(getLabOrder.type, getLabOrderSaga);
  yield takeLatest(createLabOrderNote.type, createNoteSaga);
  yield takeLatest(deleteLabOrderNote.type, deleteNoteSaga);
  yield takeLatest(updateLabOrder.type, updateLabOrderSaga);
}

export default Orders;
